import {useCallback} from 'react';
import {isEmpty} from 'lodash/fp';

import {useMultipassUrl} from '#apollo/query/useMultipassUrl';
import {useAuth} from '#hook';

const STORE_URL = process.env.NEXT_PUBLIC_STORE_URL;
const ALT_STORE_URLS = process.env.NEXT_PUBLIC_ALT_STORE_URLS;
const STORE_LOGOUT_URL_REGEXP = new RegExp(`\/pages\/logout`);

const getUrlOrigin = (url: string) => (url.match(/(https?:\/\/([^/]*))/) || [])[1] || '';

const isStoreUrl = (url: string): boolean =>
  [STORE_URL, ...(ALT_STORE_URLS || '').split(',')]
    .map(url => (url || '').trim())
    .filter(url => url && url.length > 0)
    .map(getUrlOrigin)
    .includes(getUrlOrigin(url));

const isAllowed = (url: string | null): boolean => {
  // Do not allow empty URLs or URLs without value.
  if (!url || url.trim().length <= 0) {
    return false;
  }

  // Allow internal URLs with a leading slash
  if (!!url.match(/^\//)) {
    return true;
  }

  // Allow store URLs.
  return isStoreUrl(url);
};

export const useReturnUrl = () => {
  const {isUserSignedIn} = useAuth();
  const {getMultipassUrl} = useMultipassUrl();

  const handleTargetResolver = useCallback(
    (returnUrl: string | null) => {
      return async (defaultTarget: string | null): Promise<string | null> => {
        if (!returnUrl || isEmpty(returnUrl)) {
          return defaultTarget;
        } else {
          // If the return URL is a Shopify URL ...
          if (isStoreUrl(returnUrl)) {
            // If the URL is a Shopify store logout URL ...
            if (returnUrl.match(STORE_LOGOUT_URL_REGEXP)) {
              // ... then just use the return URL with default URL as fallback.
              return returnUrl || defaultTarget;
            }
            // If the URL is not a Shopify store logout URL ...
            else {
              const isSignedIn = await isUserSignedIn({async: true});

              // If the user is currently signed in ...
              if (isSignedIn) {
                // If the return URL is not allowed ...
                if (!isAllowed(returnUrl)) {
                  // ... then treat this as if the return URL is missing.
                  return defaultTarget;
                }

                // Request a Multipass URL with the return URL.
                const multipassUrl = await getMultipassUrl(returnUrl);

                // If the Multipass URL is successfully generated ...
                if (multipassUrl) {
                  // ... then use the Multipass URL.
                  return multipassUrl;
                }
                // If the Multipass URL failed to be generated ...
                else {
                  // ... then just use the return URL with default URL as fallback.
                  return returnUrl || defaultTarget;
                }
              }
              // If the user is not currently signed in ...
              else {
                // ... then just use the return URL with default URL as fallback.
                return returnUrl || defaultTarget;
              }
            }
          }
          // If the return URL is not a Shopify URL ...
          else {
            // ... then just use the return URL with default URL as fallback.
            return returnUrl || defaultTarget;
          }
        }
      };
    },
    [getMultipassUrl, isUserSignedIn],
  );

  // A decorated version of `handleTargetResolver` that also ensure the final
  // URL is allowed.
  const handleAllowedTargetResolver = useCallback(
    (returnUrl: string | null) => {
      // Create the inner target resolver instance ...
      const resolveTarget = handleTargetResolver(returnUrl);
      return async (defaultTarget: string | null): Promise<string | null> => {
        // ... and use it to resolve the return URL.
        const url = await resolveTarget(defaultTarget);

        // And only return the resolved URL is it's allowed; Otherwise, return
        // the default target (as if the return URL is missing).
        return isAllowed(url) ? url : defaultTarget;
      };
    },
    [handleTargetResolver],
  );

  return {
    targetResolver: handleAllowedTargetResolver,
  };
};
